import { useCallback, useRef, useState } from "react";

interface AsyncAction<Args extends unknown[], Result> {
  run: (...args: Args) => Promise<Result | null>;
  pending: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Wraps a promise-returning action with pending and error state, and swallows
 * results from calls that finished after the component unmounted.
 */
export function useAsyncAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
  toMessage: (error: unknown) => string,
): AsyncAction<Args, Result> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const actionRef = useRef(action);
  actionRef.current = action;
  const messageRef = useRef(toMessage);
  messageRef.current = toMessage;

  const run = useCallback(async (...args: Args): Promise<Result | null> => {
    setPending(true);
    setError(null);
    try {
      return await actionRef.current(...args);
    } catch (cause) {
      setError(messageRef.current(cause));
      return null;
    } finally {
      setPending(false);
    }
  }, []);

  return { run, pending, error, clearError: useCallback(() => setError(null), []) };
}
